import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  Button,
  Avatar,
  CircularProgress
} from '@mui/material';
import StorageIcon from '@mui/icons-material/StorageOutlined';
import PeopleIcon from '@mui/icons-material/PeopleAltOutlined';
import PersonIcon from '@mui/icons-material/PersonOutlined';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import BusinessIcon from '@mui/icons-material/BusinessOutlined';
import UserProfileService from '../../services/UserProfileService';
import { useAccountUsers } from '../common/AccountUsersAutocomplete';

const formatDate = (value) => {
  if (!value) {
    return '?';
  }
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const SemanticDatabaseViewDialog = ({
  open,
  onClose,
  record,
  connection
}) => {
  const [userInfo, setUserInfo] = useState(null);
  const [loadingUser, setLoadingUser] = useState(false);

  const usersAllowed = useMemo(() => {
    if (!record?.users_allowed) {
      return [];
    }
    return record.users_allowed.map((item) => item?.user_id || item).filter(Boolean);
  }, [record]);
  
  const groupsAllowed = useMemo(() => {
    if (!record?.users_groups_allowed) {
      return [];
    }
    return record.users_groups_allowed.map((item) => item?.group_id || item).filter(Boolean);
  }, [record]);

  // Correo y nombre de los usuarios con acceso (el registro solo guarda ids).
  const { usersById: accountUsersById } = useAccountUsers(open && usersAllowed.length > 0);

  // Obtener información del usuario cuando se abre el diálogo
  useEffect(() => {
    const fetchUserInfo = async () => {
      if (!open || !record?.user_account_id) {
        return;
      }

      setLoadingUser(true);
      try {
        const userId = record.user_account_id || record.user_accout_id;
        const userProfile = await UserProfileService.getProfileByUserId(userId);
        
        if (userProfile) {
          // Extraer información del perfil
          const companyName = userProfile.company?.name || userProfile.company_name || '';
          const contactEmail = userProfile.company?.contact_email || '';
          
          setUserInfo({
            username: userId,
            email: contactEmail || 'No disponible',
            name: companyName || 'Usuario',
            companyName: companyName,
            fullName: companyName || 'Usuario',
            accountId: userProfile.account_id || userProfile.accountUuid
          });
        } else {
          setUserInfo(null);
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
        setUserInfo(null);
      } finally {
        setLoadingUser(false);
      }
    };

    fetchUserInfo();
  }, [open, record?.user_account_id, record?.user_accout_id]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          bgcolor: 'var(--wb-bg-panel)',
          color: 'var(--wb-text-strong)',
          borderRadius: 2
        }
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid var(--wb-overlay-10)' }}>
        Detalles de la base semántica
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {record?.name || 'Sin nombre'}
            </Typography>
            <Typography variant="body2" color="var(--wb-text-soft)" sx={{ mt: 1 }}>
              {record?.description || 'Sin descripci?n registrada.'}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="var(--wb-text-label)" sx={{ mb: 1 }}>
              Propietario
            </Typography>
            {loadingUser ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} sx={{ color: 'var(--wb-accent)' }} />
                <Typography variant="body2" color="var(--wb-text-soft)">
                  Cargando información...
                </Typography>
              </Box>
            ) : userInfo ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 2,
                  borderRadius: 1.5,
                  bgcolor: 'var(--wb-bg-overlay-60)',
                  border: '1px solid var(--wb-x-border-6366f133, rgba(99, 102, 241, 0.2))'
                }}
              >
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: 'var(--wb-x-bg-6366f133, rgba(99, 102, 241, 0.2))',
                    color: 'var(--wb-x-text-818cf8, #818cf8)',
                    fontSize: 20,
                    fontWeight: 600
                  }}
                >
                  {userInfo.fullName.charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: 'var(--wb-text)', 
                      fontWeight: 600,
                      mb: 0.5
                    }}
                  >
                    {userInfo.fullName}
                  </Typography>
                  {userInfo.companyName && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                      <BusinessIcon sx={{ fontSize: 14, color: 'var(--wb-muted)' }} />
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'var(--wb-muted)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {userInfo.companyName}
                      </Typography>
                    </Box>
                  )}
                  {userInfo.email && userInfo.email !== 'No disponible' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                      <EmailIcon sx={{ fontSize: 14, color: 'var(--wb-muted)' }} />
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'var(--wb-muted)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {userInfo.email}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PersonIcon sx={{ fontSize: 14, color: 'var(--wb-muted)' }} />
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'var(--wb-muted)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      ID: {userInfo.username}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 2,
                  borderRadius: 1.5,
                  bgcolor: 'var(--wb-bg-overlay-60)',
                  border: '1px solid var(--wb-border-20)'
                }}
              >
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: 'var(--wb-border-20)',
                    color: 'var(--wb-muted)'
                  }}
                >
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="var(--wb-text-soft)">
                    Usuario no disponible
                  </Typography>
                  <Typography variant="caption" color="var(--wb-text-dim)">
                    ID: {record?.user_account_id || record?.user_accout_id || '?'}
                  </Typography>
                </Box>
              </Box>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="var(--wb-text-label)" sx={{ mb: 0.5 }}>
              Identificadores
            </Typography>
            <Typography variant="body2" color="var(--wb-text-soft)">
              ID: {record?.semantic_database_id || '?'}
            </Typography>
            <Typography variant="body2" color="var(--wb-text-soft)">
              Cuenta: {record?.account_id || '?'}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="var(--wb-text-label)" sx={{ mb: 0.5 }}>
              Estado
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={record?.is_active === false ? 'Inactiva' : 'Activa'}
                size="small"
                sx={{ color: 'var(--wb-success-strong)', backgroundColor: 'var(--wb-x-bg-34d39924, rgba(52, 211, 153, 0.14))' }}
              />
              {record?.is_public && (
                <Chip
                  label="P?blica"
                  size="small"
                  sx={{ color: 'var(--wb-accent)', backgroundColor: 'var(--wb-x-bg-38bdf824, rgba(56, 189, 248, 0.14))' }}
                />
              )}
              {record?.is_deleted && (
                <Chip
                  label="Marcada como eliminada"
                  size="small"
                  sx={{ color: 'var(--wb-danger)', backgroundColor: 'var(--wb-danger-16)' }}
                />
              )}
            </Box>
            <Typography variant="body2" color="var(--wb-text-soft)" sx={{ mt: 1 }}>
              Versión: {record?.version_id || '?'}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" color="var(--wb-text-label)" sx={{ mb: 1.5 }}>
              Conexión asociada
            </Typography>
            {connection ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 2,
                  borderRadius: 1.5,
                  bgcolor: 'var(--wb-bg-overlay-60)',
                  border: '1px solid var(--wb-x-border-38bdf833, rgba(56, 189, 248, 0.2))'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 44,
                    height: 44,
                    borderRadius: 1,
                    bgcolor: 'var(--wb-x-bg-38bdf826, rgba(56, 189, 248, 0.15))',
                    border: '1px solid var(--wb-accent-30)',
                    flexShrink: 0
                  }}
                >
                  <StorageIcon sx={{ color: 'var(--wb-accent)', fontSize: 22 }} />
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: 'var(--wb-text)', 
                      fontWeight: 600,
                      mb: 0.5
                    }}
                  >
                    {connection.connection_name || connection.enterprise_application_name || 'Sin nombre'}
                  </Typography>
                  {connection.connection_type && (
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        bgcolor: 'var(--wb-x-bg-38bdf826, rgba(56, 189, 248, 0.15))',
                        border: '1px solid var(--wb-accent-30)'
                      }}
                    >
                      <Typography 
                        sx={{ 
                          color: 'var(--wb-accent)', 
                          fontSize: 12, 
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5
                        }}
                      >
                        {connection.connection_type}
                      </Typography>
                    </Box>
                  )}
                  {connection.database_name && (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'var(--wb-muted)',
                        display: 'block',
                        mt: 0.5
                      }}
                    >
                      Base de datos: {connection.database_name}
                    </Typography>
                  )}
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 2,
                  borderRadius: 1.5,
                  bgcolor: 'var(--wb-bg-overlay-60)',
                  border: '1px solid var(--wb-border-20)'
                }}
              >
                <StorageIcon sx={{ color: 'var(--wb-x-text-64748b, #64748b)' }} />
                <Typography variant="body2" color="var(--wb-text-dim)">
                  Conexión no encontrada
                </Typography>
              </Box>
            )}
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" color="var(--wb-text-label)" sx={{ mb: 1 }}>
              Usuarios con acceso
            </Typography>
            {usersAllowed.length === 0 ? (
              <Typography variant="body2" color="var(--wb-text-soft)">
                No hay usuarios registrados.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {usersAllowed.map((userId) => {
                  const user = accountUsersById.get(userId);
                  return (
                    <Chip
                      key={userId}
                      label={user?.email || user?.name || userId}
                      title={user?.name ? `${user.name} · ${user.email || userId}` : undefined}
                      icon={<PeopleIcon />}
                      sx={{ bgcolor: 'var(--wb-border-20)', color: 'var(--wb-text)' }}
                    />
                  );
                })}
              </Box>
            )}
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" color="var(--wb-text-label)" sx={{ mb: 1 }}>
              Grupos con acceso
            </Typography>
            {groupsAllowed.length === 0 ? (
              <Typography variant="body2" color="var(--wb-text-soft)">
                No hay grupos registrados.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {groupsAllowed.map((groupId) => (
                  <Chip
                    key={groupId}
                    label={groupId}
                    sx={{ bgcolor: 'var(--wb-x-bg-38bdf829, rgba(56, 189, 248, 0.16))', color: 'var(--wb-x-text-bae6fd, #bae6fd)' }}
                  />
                ))}
              </Box>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="var(--wb-text-label)" sx={{ mb: 0.5 }}>
              Auditoría
            </Typography>
            <Typography variant="body2" color="var(--wb-text-soft)">
              Creado por: {record?.audit?.created_by || '?'}
            </Typography>
            <Typography variant="body2" color="var(--wb-text-soft)">
              Actualizado por: {record?.audit?.updated_by || '?'}
            </Typography>
            <Typography variant="body2" color="var(--wb-text-soft)">
              Creado el: {formatDate(record?.audit?.created_at)}
            </Typography>
            <Typography variant="body2" color="var(--wb-text-soft)">
              Actualizado el: {formatDate(record?.audit?.updated_at)}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="var(--wb-text-label)" sx={{ mb: 0.5 }}>
              Soft delete
            </Typography>
            <Typography variant="body2" color="var(--wb-text-soft)">
              Eliminado: {record?.soft_delete?.deleted ? 'S?' : 'No'}
            </Typography>
            <Typography variant="body2" color="var(--wb-text-soft)">
              Fecha de eliminación: {formatDate(record?.soft_delete?.deleted_at)}
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid var(--wb-overlay-10)', p: 2 }}>
        <Button onClick={onClose} sx={{ color: 'var(--wb-text-label)' }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SemanticDatabaseViewDialog;
